import { useCallback } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useCampaignWizardStore } from '../../stores/campaign-wizard-store';
import { useSaveDraft, useUpdateDraft, useLaunchCampaign } from '../../api/campaigns-mutations';
import type { CampaignWizardFormData, WizardStep } from '../../types/campaigns.types';
import { WIZARD_STEPS } from '../../types/campaigns.types';

/**
 * Orchestrates the campaign creation wizard.
 * Coordinates wizard store state with API mutations.
 */
export function useCampaignWizard() {
  const store = useCampaignWizardStore();
  const saveDraftMutation = useSaveDraft();
  const updateDraftMutation = useUpdateDraft();
  const launchMutation = useLaunchCampaign();
  const navigate = useNavigate();

  const currentStepIndex = WIZARD_STEPS.indexOf(store.currentStep);
  const canGoNext = store.currentStep !== 'review-launch';
  const canGoPrev = store.currentStep !== WIZARD_STEPS[0];
  const totalSteps = WIZARD_STEPS.length;

  const goToStep = useCallback(
    (step: WizardStep) => {
      store.setStep(step);
    },
    [store],
  );

  const saveDraft = useCallback(async () => {
    const { draftCampaignId, formData } = store;
    if (draftCampaignId) {
      const result = await updateDraftMutation.mutateAsync({
        id: draftCampaignId,
        payload: formData,
      });
      return result;
    } else {
      const result = await saveDraftMutation.mutateAsync(formData);
      store.setDraftCampaignId(result.id);
      return result;
    }
  }, [store, saveDraftMutation, updateDraftMutation]);

  const launch = useCallback(async () => {
    store.setSubmitting(true);
    try {
      await launchMutation.mutateAsync({
        payload: store.formData as CampaignWizardFormData,
        // Draft saved via "Save draft" gets archived once the launch
        // succeeds — the launched campaign replaces it.
        supersededDraftId: store.draftCampaignId,
      });
      store.reset();
      navigate({ to: '/promote/campaigns' });
    } finally {
      store.setSubmitting(false);
    }
  }, [store, launchMutation, navigate]);

  return {
    ...store,
    currentStepIndex,
    canGoNext,
    canGoPrev,
    totalSteps,
    goToStep,
    saveDraft,
    launch,
    isSaving: saveDraftMutation.isPending || updateDraftMutation.isPending,
    isLaunching: launchMutation.isPending,
  };
}
